import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiOutlineClose } from 'react-icons/ai';
import { toast } from 'react-toastify';

import { type OwnerType } from '../../../common/constants';
import { extractErrorsFromApi } from '../../../common/helpers';
import RenderErrorBag from '../../../components/render-error-bag';
import { useBulkSyncReceiptMutation } from '../receipt.api-slice';

interface IProps {
  isOpen: boolean;
  toggleModal: () => any;
  ownerEntityType?: OwnerType;
  ownerEntityId?: string;
}

const BulkSyncReceipts: React.FC<IProps> = ({ isOpen, toggleModal, ownerEntityId, ownerEntityType }) => {
  const [bulkSyncReceipts, { isSuccess, data, isLoading, isError, error, reset }] = useBulkSyncReceiptMutation();
  const [errorBag] = useState<string[]>([]);
  const { t } = useTranslation();

  const handleConfirmation = (): void => {
    void bulkSyncReceipts({ ownerEntityId, ownerEntityType });
    toggleModal();
  };

  useEffect(() => {
    if (isSuccess) {
      if (data?.stats != null) {
        toast(
          `Total: ${data.stats.totalProcessed}, success: ${data.stats.success}, failed: ${data.stats.failed}, skipped: ${data.stats.skipped}`
        );
      }
      reset();
    }
    if (isError && error != null) {
      toast.error(extractErrorsFromApi(error).toString);
      reset();
    }
  }, [isSuccess, error]);

  return (
    <>
      {isOpen ? (
        <>
          <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden outline-none focus:outline-none">
            <div className="mx-auto my-6 w-1/2">
              <div className="flex flex-col rounded-lg border-0 bg-white shadow-lg outline-none focus:outline-none">
                <div className="flex items-start justify-between rounded-t border-b border-solid border-slate-200 p-5">
                  <h3 className="text-3xl font-semibold">{t('receipts.bulkSync.title')}</h3>
                  <button
                    className="float-right ml-auto border-0 p-1 text-3xl font-semibold leading-none text-black outline-none focus:outline-none"
                    onClick={toggleModal}
                  >
                    <AiOutlineClose size={20} />
                  </button>
                </div>
                <div className="relative w-full flex-auto p-6">
                  {errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
                  {t('receipts.bulkSync.body')}
                </div>
                <div className="mt-4 flex w-full justify-end space-x-2 rounded-b border-t border-solid border-slate-200 px-4 py-6">
                  <button className="rounded-lg bg-gray-200 px-5 py-2 text-black" onClick={toggleModal}>
                    {t('receipts.bulkSync.cancelBtnLabel')}
                  </button>
                  <button
                    disabled={isLoading}
                    onClick={handleConfirmation}
                    className="rounded-lg bg-greenColor px-4 py-2 text-white"
                  >
                    {t('receipts.bulkSync.saveBtnLabel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="fixed inset-0 z-40 bg-black opacity-25"></div>
        </>
      ) : null}
    </>
  );
};

export default BulkSyncReceipts;
